import { TabsProps, TabProps, Tabs, Tab } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Box, styled } from '@/theme/index';

export interface TabData {
  label: string;
  icon?: JSX.Element;
  iconPosition?: string;
  content: JSX.Element;
  trackClick?: () => void;
  customHandler?: () => void;
}

type TabsNavigationProps = Omit<TabsProps, 'onFocusVisible'> & {
  tabs: TabData[];
  tabProps?: TabProps;
  container?: React.ElementType;
  tab?: React.ElementType;
  tabPanel?: React.ElementType;
  onTabChanged?: (activeTab: number) => void;
  // This is used to control the active tab from outside of the component
  externalActiveTab?: number;
  allowScrollButtonsMobile?: boolean;
};

export const StyledTab = styled(Tab)(({ theme }) => ({
  color: theme.palette.success.main,
  textDecoration: 'underline',
  fontWeight: 'normal',
  borderBottom: 'none',
  paddingBottom: '0',
  // Accessing the custom attributes
  '&[data-active-tab="true"]': {
    color: theme.palette.primary.dark,
    fontWeight: 'bold',
    borderBottom: '4px solid #2D2D39',
    paddingBottom: '10px',
  },
}));

export function TabsNavigation({
  tabs,
  tab,
  tabProps,
  container,
  tabPanel,
  onTabChanged,
  externalActiveTab = 0,
  allowScrollButtonsMobile = true,
  ...rest
}: TabsNavigationProps) {
  const [activeTab, setActiveTab] = useState(
    typeof externalActiveTab === 'number' ? externalActiveTab : 0
  );
  const [initialTabsLength, setInitialTabsLength] = useState(tabs?.length);

  useEffect(() => {
    if (
      typeof externalActiveTab === 'number' &&
      externalActiveTab !== activeTab
    ) {
      setActiveTab(externalActiveTab);
    }
  }, [externalActiveTab]);

  if (tabs?.length !== initialTabsLength) {
    setActiveTab(0);
    setInitialTabsLength(tabs?.length);
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    // This piece of code is for custom handler such as opening a modal, instead of showing content as it normal would.
    if (tabs[newValue]?.customHandler) {
      tabs[newValue].customHandler?.();
      return;
    }

    setActiveTab(newValue);
    typeof onTabChanged === 'function' && onTabChanged(newValue);
  };
  const Container = container || Box;
  const StyledTabHeader = tab || StyledTab;
  const StyledTabPanel = tabPanel || Box;

  const currentTabPanel = () => {
    const currentTab = tabs?.[activeTab];
    return <StyledTabPanel>{currentTab?.content}</StyledTabPanel>;
  };

  return (
    <div>
      <Container>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile={allowScrollButtonsMobile}
          {...rest}
          textColor="primary"
        >
          {tabs?.map((tab, index) => (
            <StyledTabHeader
              key={index}
              label={tab?.label}
              icon={tab?.icon}
              iconPosition={tab?.iconPosition || 'start'}
              data-active-tab={activeTab === index}
              {...tabProps}
              onClick={tab?.trackClick}
            />
          ))}
        </Tabs>
      </Container>
      {currentTabPanel()}
    </div>
  );
}
